import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { Box, Flex, Input, Stack, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  closeCreateInitiativePanelAction,
  openCreateInitiativePanelAction,
} from 'actions/createInitiativesPanelActions';
import MeshListContext from 'components/MeshList/MeshListContext';
import { openPanel } from 'components/RabbitInitiativeCreationPanel/InitiativeCreationPanelSlice';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useEmployeeGoalsPrivileges } from 'components/hooks/useEmployeeGoalsPrivileges';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { useMyInitiativeTableData } from 'screens/Initiatives/Hooks/dataHooks';
import AGGridTable from 'screens/Initiatives/InitiativeAudit/AGGrid';
import { selectCreateInitiativePanelData } from 'selectors/createInitiativePanelSelector';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectTaskDetails } from 'selectors/taskDetails';
import { baseTheme } from 'themes/clients/baseTheme';

import { PlusNotOutlinedWhite } from 'assets/assetsV1/iconComponents';
import NoInitiatives from 'assets/assetsV1/illustrations/newIllustrations/noInitiatives.svg';

import { thisYear, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendClickedOnAddNewButton, sendOpenedInitiativePanel, sendPageViewEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';

import { useInitiativeTypeValues } from './hooks/useInitiativeTypeValues';
import { useSelectInitiativeSelectedIntent } from './slice/InitiativeIntentSlice';

const TABLE_FILTERS = [
  {
    type: 'STATUS',
    value: 'ALL',
    displayValue: 'All',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'ONGOING',
    displayValue: 'On-going',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'DELAYED',
    displayValue: 'Delayed',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'UNPLANNED',
    displayValue: 'Unplanned',
    operator: 'IN',
  },

  {
    displayValue: 'Completed',
    operator: 'IN',
    type: 'STATUS',
    value: 'COMPLETED',
  },
];

const RenderTable = ({
  isLoading,
  data,
  openCreateInitiativeSidePanel,
  btnRef,
  canCreateInitiativePrivilege = true,
  gridStatePersistenceKey = null,
  gridRef,
}) => {
  const img = NoInitiatives;

  const noInitiativesHeading = 'Initiatives are activities that you complete to achieve your competencies';

  const noInitiativesDescription = 'Take the initiative and get started now';

  if (isLoading) {
    return (
      <Stack>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }

  if (data?.computedData?.length > 0) {
    return (
      <AGGridTable
        gridStatePersistenceKey={gridStatePersistenceKey}
        tableData={data?.computedData}
        filtersArray={TABLE_FILTERS}
        gridRef={gridRef}
      />
    );
  }

  return (
    <Flex justifyContent="center">
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading={noInitiativesHeading}
          description={noInitiativesDescription}
          illustrationSrc={img}
          // eslint-disable-next-line react/no-unstable-nested-components
          CTAComp={() => {
            if (canCreateInitiativePrivilege)
              return (
                <Button onClick={openCreateInitiativeSidePanel} StartIcon={PlusNotOutlinedWhite} ref={btnRef}>
                  Add New
                </Button>
              );
            return null;
          }}
        />
      </div>
    </Flex>
  );
};

export const updatedThisYear = {
  endDate: thisYear.endDate.valueOf(),
  startDate: thisYear.startDate.valueOf(),
  label: thisYear.label,
};

function InitiativeIntentLandingPage({ dateRange: dateRangeFromProps, gridRef }) {
  const queryClient = useQueryClient();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const [searchText, setSearchText] = useState('');
  const cohortDetails = useSelectedView();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [createNewInProgress, setCreateNewInProgress] = useState(false);

  const INITIATIVES_TYPES = useInitiativeTypeValues();

  const { selectedInitiativeType, selectedDateRange } = useSelectInitiativeSelectedIntent() ?? {};

  const { data: myInitiativesData, isLoading: isLoadingOKRInitiatives } = useMyInitiativeTableData({
    type: selectedInitiativeType?.id ?? INITIATIVES_TYPES[0]?.id,
    dateRange: selectedDateRange || dateRangeFromProps,
    enabled: cohortDetails?.type === 'MY_INITIATIVES',
  });

  const { isOpen } = useSelector(selectCreateInitiativePanelData, shallowEqual);
  const { display } = useSelector(selectTaskDetails);
  const page = useViewPageName();

  const btnRef = React.useRef();
  const dispatch = useDispatch();
  const contextValue = useMemo(
    () => ({
      dispatch,
      tasksFilters: {},
      selectedTeamsPageView: {},
      setCreateNewInProgress,
    }),
    [dispatch]
  );

  useEffect(() => {
    // display is controlling whether initiatives is being viewed/updated
    // isOpen is controlling whether add new initiatives side-panel is open/close
    // @shreyansh: shukriya
    if (!display || !isOpen) {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
    }
  }, [display, queryClient, isOpen]);

  const { canCreateInitiativePrivilege } = useEmployeeGoalsPrivileges();
  const isInitiativeCreationAllowed = canCreateInitiativePrivilege;

  const handleNewInitiativeSave = useCallback(() => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.MyInitiativeTableData]);
  }, [queryClient]);

  const closeCreateInitiativeSidePanel = useCallback(() => {
    dispatch(closeCreateInitiativePanelAction());
    handleNewInitiativeSave();
  }, [dispatch, handleNewInitiativeSave]);

  const openCreateInitiativeSidePanel = useCallback(() => {
    sendClickedOnAddNewButton({
      eventProperties: {
        type: 'Initiative',
        page,
      },
    });

    const updatedToIds = [
      {
        uuid: loggedInEmployee.id,
        ownerType: 'OWNER',
        metadata: { displayName: loggedInEmployee.displayName },
        profile: { profilePhoto: loggedInEmployee.profilePhotoSrc },
      },
    ];

    dispatch(closeSecondaryPanel(null)); /// // WILL CLOSE SECONDARY DRAWER ON INITIAL OPENING ON ADD NEW INITIATIVE PANEL
    sendOpenedInitiativePanel({
      eventProperties: {
        page,
        source: 'Add new button',
      },
    });
    dispatch(
      openPanel({
        toIds: updatedToIds,
      })
    );

    dispatch(
      openCreateInitiativePanelAction({
        onClose: closeCreateInitiativeSidePanel,
        onCancel: closeCreateInitiativeSidePanel,
        team: cohortDetails,
        onSave: handleNewInitiativeSave,
        selectedTimeBoundPeriod: {},
        ownerEmployee: loggedInEmployee,
        subObjective: null,
        baseKR: null,
      })
    );
  }, [page, loggedInEmployee, dispatch, closeCreateInitiativeSidePanel, cohortDetails, handleNewInitiativeSave]);

  const onSearchInputChange = (e) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (searchText !== null && gridRef?.current?.api) {
      gridRef?.current?.api?.setGridOption('quickFilterText', searchText);
    }
  }, [gridRef, searchText]);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: 'Me-initiatives-manage',
      },
    });
  }, []);

  /* Grid State persistence Key */
  const isOneOnOneInitiatives = selectedInitiativeType?.id === 'ONE_ON_ONE';
  const viewId = cohortDetails?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.TASK;
    const tabId = null;
    const uniqueId = isOneOnOneInitiatives ? '1on1_Initiatives_Grid' : 'Goals_Initiatives_Grid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [isOneOnOneInitiatives, viewId]);

  return (
    <MeshListContext.Provider value={contextValue}>
      <Box p={0}>
        <Paper p={3} mb={2} overflowX="auto" boxShadow={baseTheme.shadows.paper} borderRadius="8px">
          <Flex
            style={{ marginBottom: '16px' }}
            justifyContent="space-between"
            alignItems={!isMobileView() && 'center'}
            flexWrap="wrap"
            flexDirection={isMobileView() ? 'column' : 'row'}
            minH="40px"
          >
            <Input
              placeholder="Search title"
              sx={{ maxWidth: '360px' }}
              value={searchText}
              onChange={onSearchInputChange}
            />
            {!['ONE_ON_ONE', 'COMPETENCY_CHECKIN', 'NUDGE_TASK'].includes(selectedInitiativeType?.id) &&
              isInitiativeCreationAllowed && (
                <Button
                  onClick={openCreateInitiativeSidePanel}
                  StartIcon={PlusNotOutlinedWhite}
                  ref={btnRef}
                  marginTop="10px"
                >
                  Add
                </Button>
              )}
          </Flex>
          <RenderTable
            btnRef={btnRef}
            gridStatePersistenceKey={gridStatePersistenceKey}
            data={myInitiativesData}
            isLoading={isLoadingOKRInitiatives}
            openCreateInitiativeSidePanel={openCreateInitiativeSidePanel}
            canCreateInitiativePrivilege={
              !['ONE_ON_ONE', 'COMPETENCY_CHECKIN', 'NUDGE_TASK'].includes(selectedInitiativeType?.id) &&
              isInitiativeCreationAllowed
            }
            gridRef={gridRef}
          />
        </Paper>
      </Box>
    </MeshListContext.Provider>
  );
}

export { InitiativeIntentLandingPage };
